/**
 * Wire contract for the audio-batch (`/api/v1/audio-batches`) transcription
 * surfaces: submit, status polling, transcript lists, segment views/suggestions,
 * and the client-facing validation caps.
 *
 * SERVER-BOUND (was CLIENT-DECLARED until #606): every response type below is
 * annotated in apps/api — `serializeItem` (`batch_item_media_service.ts`) for
 * item payloads (`AudioBatchItem`), `serializeSegment`
 * (`transcript_segment_edit_service.ts`) for cue views, `serializeEditableVisualSegment`
 * (`visual_segment_summaries.ts`) for `VisualSegmentView`, and inline
 * contract-typed consts across the four audio-batch controllers for the
 * envelopes — `audio_batches_controller` (me / history list / segments /
 * status), `audio_batch_submissions_controller` (submit + the upload-staging
 * initiate / part / complete / abort), `audio_batch_items_controller`
 * (share / unshare / suggestion review), `audio_batch_segments_controller`
 * (split / merge), and `audio_batch_lifecycle_controller` (batch + item delete /
 * audio purge / video abandon / retry).
 *
 * Still NOT compiler-enforced, by design:
 * - REQUEST bodies of the segment editor (split/merge/patch/suggestion-review)
 *   and the multipart staging calls are hand-parsed in the controller (no vine
 *   validator); their request side stays unchecked.
 * - The audio-editing cluster (`editSuccessPayload`, insert/revoice/tts-preview/
 *   export/delete-segments) has no declared envelope yet — bind before promoting
 *   any client-side copy of those shapes.
 */
import { TERMINAL_AUDIO_BATCH_ITEM_STATUSES, type AudioBatchDisplayStatus, type AudioBatchStatus, type TranscriptionFailureCode, type TranscriptRetentionPolicy, type OrganizationKind, type PlanCode, type MediaKind } from '@kiao-voice/core'
import type { ApiErrorBody } from './errors.js'

/**
 * Status taken straight from the backend `video_transcription_jobs.status`
 * column. Kept verbatim so a stringly-typed comparison still works if the
 * backend ever returns a status the UI hasn't been taught about — falling
 * through to the "unknown" badge is better than crashing the page.
 */
export type AudioBatchItemStatus =
  | 'pending'
  | 'downloading'
  | 'extracting_audio'
  | 'audio_ready'
  | 'transcribing'
  | 'enhancing'
  | 'completed'
  | 'failed'

export const TERMINAL_ITEM_STATUSES: ReadonlySet<AudioBatchItemStatus> = new Set(
  TERMINAL_AUDIO_BATCH_ITEM_STATUSES
)

/** `batch_jobs.status`; the value list lives in `@kiao-voice/core`. */
export type { AudioBatchStatus }

export interface TranscriptionUserSummary {
  id: number
  email: string | null
}

export interface TranscriptionCapabilities {
  canView: boolean
  canEditContent: boolean
  canManageNotes: boolean
  canEditAudio: boolean
  canDelete: boolean
  canShareToTeam: boolean
  canUnshare: boolean
  canManagePublicLink: boolean
}

export interface TranscriptionSharingMetadata {
  sharedWithOrganizationId?: number | null
  sharedAt?: string | null
  uploader?: TranscriptionUserSummary | null
  lastEditedBy?: TranscriptionUserSummary | null
  permissions?: TranscriptionCapabilities | null
}

/**
 * `GET /api/v1/audio-batches/me` — the access probe a signed-in client makes
 * before using batch transcription. Measured against
 * `audio_batches_controller.me`: every field below is ALWAYS emitted.
 * `plan` is the workspace's EFFECTIVE plan code (`effectivePlanForUser`), so a
 * team context answers `'team'` here — only `/auth/me` collapses tiers to the
 * client-safe set. `probeInternalAccess` still narrows defensively on top.
 */
export interface AudioBatchMeResponse {
  ok: true
  email: string
  plan: PlanCode
  maxFileMinutes: number | null
  maxFileBytes: number
  taigiEnabled: boolean
}

/**
 * `POST /api/v1/audio-batches/preflight` — browser-measured upload estimate.
 * 估算；不保留額度；`ok: true` 不構成保證，worker gate 仍可因跨 batch 併發、
 * 時長量測誤差拒絕。Do not remove the authoritative worker gate.
 */
export interface AudioBatchPreflightRequest {
  /** 與上傳佇列同序，1–AUDIO_BATCH_MAX_FILES 筆；瀏覽器量不到時傳 null。 */
  files: Array<{ durationSecs: number | null }>
  visualContext?: boolean
}

/**
 * 估算；不保留額度；`ok: true` 不構成保證，worker gate 仍可拒絕
 * （跨 batch 併發、時長量測誤差）。Normal affordability results return HTTP 200.
 * All credit amounts describe this request only and must not be persisted.
 */
export type AudioBatchPreflightResponse =
  | {
      ok: true
      totalDurationSecs: number
      creditsNeeded: number | null
      creditsAvailable: number | null
    }
  | {
      ok: false
      reason: 'insufficient_credits'
      totalDurationSecs: number
      creditsNeeded: number
      creditsAvailable: number
    }
  | {
      ok: false
      reason: 'file_too_long_for_plan'
      fileIndex: number
      durationSecs: number
      maxFileMinutes: number
      plan: AudioBatchMeResponse['plan']
    }

/** `POST /audio-batches/:id/items/:itemId/retry` — HTTP 402 / cap HTTP 409. */
export type AudioBatchRetryRefusalBody = ApiErrorBody &
  (
    | {
        error: 'insufficient_credits'
        /** Stable English label; clients render localized copy by error code. */
        message: string
        /** Action-time balance only; never persist it on the failed item. */
        balance: number
      }
    | {
        error: 'file_too_long_for_plan'
        message: string
        maxFileMinutes: number
        plan: AudioBatchMeResponse['plan']
      }
  )

export interface AudioBatchItem extends TranscriptionSharingMetadata {
  id: number
  batchId: number
  filename: string | null
  status: AudioBatchItemStatus
  /** Human display string from the API — English-only, do NOT render directly. */
  errorMessage: string | null
  /** Stable machine-readable failure reason; map to a localized message. */
  errorCode: TranscriptionFailureCode | null
  audioDurationSecs: number | null
  startedAt: string | null
  completedAt: string | null
  /**
   * When this item's media becomes eligible for permanent deletion
   * (`completedAt + 30 days`); null until completed. Drives the detail-page
   * expiry banner. Added by the backend `serializeItem`.
   */
  expiresAt: string | null
  /**
   * What `expiresAt` takes away. `purge_all` removes the transcript along with
   * the audio; `retain_transcript` (paid plans) removes only the audio. The
   * date alone cannot say which, so the copy has to branch on this.
   */
  retentionPolicy: TranscriptRetentionPolicy
  /**
   * Raw editor attribution (user id of the last content edit). The display
   * name/email travels in {@link TranscriptionSharingMetadata.lastEditedBy};
   * this raw id is what `serializeItem` emits alongside it.
   */
  lastEditedByUserId: number | null
  /**
   * Live intra-stage progress for an item still being worked on, or null when
   * there is none to report (terminal item, or a row that predates the feature).
   * OPTIONAL because only the polling endpoint (`GET /audio-batches/:id`) pays
   * the cost of assembling it — the submit/share/retry payloads reuse
   * `serializeItem` and simply omit the key, so a client must treat "absent"
   * and "null" identically.
   *
   * This exists because `status` alone cannot say how far along the item is:
   * `transcribing` covers speech recognition, cue segmentation, polishing and
   * persistence, which together are the longest stretch of the pipeline.
   */
  progress?: AudioBatchItemProgress | null
}

/**
 * The finer-grained step inside the `transcribing` status. Product language, in
 * pipeline order — deliberately NOT a vendor or model name, and not a mirror of
 * any internal stage identifier.
 */
export type AudioBatchItemSubStage =
  | 'preparing_audio'
  | 'stt'
  | 'cue_segmentation'
  | 'enhancement'
  | 'finalizing'

/**
 * What the server knows about how far the current stage has got. Every field is
 * independently nullable because each answers a different question and they go
 * unknown at different times.
 */
export interface AudioBatchItemProgress {
  /**
   * Which step inside `transcribing` is running. Null before the item reaches
   * `transcribing` — the coarse stage is then read off `status` instead.
   */
  subStage: AudioBatchItemSubStage | null
  /**
   * 0..1 within the current step, and ONLY when the work is genuinely countable
   * (uploaded chunks, LLM windows, processed tokens). Null means "running, but
   * the server cannot honestly quantify it" — the client interpolates from
   * {@link expectedSecs} rather than rendering a made-up number.
   */
  fraction: number | null
  /**
   * How long this step usually takes, in seconds, derived from this
   * deployment's own recorded stage timings. Null when there aren't enough
   * samples to say. It is an interpolation constant, NOT an ETA: never render
   * it as remaining time.
   */
  expectedSecs: number | null
  /**
   * When the pipeline last wrote this progress. Null when the current stage has
   * not reported yet; the client then measures elapsed time from
   * {@link AudioBatchItem.startedAt}.
   */
  updatedAt: string | null
  /**
   * When the CURRENT sub-stage was entered. Separate from {@link updatedAt},
   * which moves every couple of seconds — measuring "how long has this step been
   * running" from that would reset the clock on every write. Server-sent rather
   * than accumulated client-side so it survives a reload and is correct for a
   * viewer who opens the page mid-run.
   */
  subStageStartedAt: string | null
}

/**
 * Whether the retention window takes the transcript too, or only the audio.
 * Stamped on the batch at submission time from the plan then in force. The
 * value list lives in `@kiao-voice/core`.
 */
export type { TranscriptRetentionPolicy }

export interface AudioBatchSubmitResponse {
  batchId: number
  status: AudioBatchStatus
  totalItems: number
  items: AudioBatchItem[]
}

export interface AudioBatchStatusResponse {
  id: number
  status: AudioBatchStatus
  totalItems: number
  completedItems: number
  failedItems: number
  createdAt: string
  updatedAt: string
  items: AudioBatchItem[]
}

/**
 * Coarse display status the history list + detail doc-head show per file. The
 * value list lives in `@kiao-voice/core`.
 */
export type { AudioBatchDisplayStatus }

/**
 * One row in the "my transcriptions" history list (`GET /audio-batches`).
 * The list is FLATTENED to one row per FILE (item), not per batch — a multi-file
 * upload shows as several rows. `title` is the file's name (null when unknown).
 */
export interface AudioTranscriptListItem extends TranscriptionSharingMetadata {
  /**
   * Non-null only for a failed underlying item; the cancelled display bucket
   * carries `cancelled_by_user`. Map this code to a localized message instead
   * of rendering an API errorMessage.
   */
  errorCode: TranscriptionFailureCode | null
  batchId: number
  itemId: number
  title: string | null
  durationSecs: number | null
  status: AudioBatchDisplayStatus
  createdAt: string
  updatedAt: string
  /**
   * When this row's media is permanently deleted (`completedAt + 30 days`);
   * null unless completed. Powers the per-row deletion countdown. The backend
   * already floats expiring rows to the top.
   */
  expiresAt: string | null
  /** See {@link AudioBatchItem.retentionPolicy}. */
  retentionPolicy: TranscriptRetentionPolicy
  /**
   * See {@link AudioBatchItem.progress}. Present here so a freshly loaded list
   * can render a processing row's stage — and a FAILED row's freeze point —
   * without waiting for, or in the failed case ever getting, a status poll.
   */
  progress?: AudioBatchItemProgress | null
  /**
   * How many `done` 整理 (meeting notes) this item has. The app shows a
   * "記錄" affordance when this is `> 0`; a `queued`/`running`/`failed` note
   * does not count until it settles.
   */
  doneNoteCount: number
  /**
   * Distinct non-null speaker codes seen in this item's transcript segments;
   * `0` when there is no transcript yet (or diarization never ran).
   */
  speakerCount: number
}

export interface AudioTranscriptListResponse {
  scope?: OrganizationKind
  page: number
  perPage: number
  total: number
  items: AudioTranscriptListItem[]
}

/** A roster entry. `displayName` null → render the default `Speaker {code}` label. */
export interface TranscriptSpeaker {
  code: string
  displayName: string | null
}

/**
 * One transcript segment as served to the viewer/editor. `id` is the DB
 * primary key (used to reassign the segment to another speaker); `index` is
 * the ordinal within the item; `speaker` is the roster code or null.
 */
/**
 * One rendered word of a cue and when it is spoken, in seconds. The words of a
 * cue cover its `text` exactly — `concat(words[].text) === text`, punctuation
 * and spacing included — so the text can be sliced by them with no alignment
 * logic on the client.
 */
export interface TranscriptWord {
  text: string
  start: number
  end: number
}

export interface TranscriptSegmentView {
  id: number
  index: number
  start: number
  end: number
  text: string
  /**
   * Real acoustic word timing, aligned by the API against `text`. `null` when
   * the cue has no ground truth (jobs transcribed before word timing existed,
   * providers without it, AI-generated cues) — the client estimates those
   * itself rather than the server inventing times. May be absent entirely when
   * talking to an API that predates the field.
   */
  words?: TranscriptWord[] | null
  rawText: string
  speaker: string | null
  /** True when this cue's audio was AI-generated (補句 TTS / 重新配音). */
  isAiGenerated?: boolean
  /** Speaker code whose voice was cloned for this AI cue; null otherwise. */
  aiVoiceSpeaker?: string | null
}

/**
 * One AI-proposed correction surfaced for human review on a video transcript —
 * the backend located a likely mis-transcription against the on-screen text
 * (slides / captions the frame OCR read). Content-addressed by `(startMs,
 * originalText)`: `originalText` is the transcript span the correction targets,
 * `suggestedText` the proposed replacement (null or empty → reference-only,
 * nothing to one-click apply). The window `[startMs, endMs)` is used to locate
 * the cue it surfaces on. The reviewer accepts (exact-substring replace) or
 * skips, and the owner-scoped correction id lets the page persist that
 * disposition so handled suggestions stay out of future responses.
 */
export interface TranscriptSuggestion {
  /** Owner-scoped correction id used to persist accept/reject review state. */
  id: number
  startMs: number
  endMs: number
  originalText: string
  suggestedText: string | null
}

/**
 * One on-screen (visual) transcript segment for the video editor's synced
 * visual track — the AI's reading of what's on screen (slides / captions) for
 * the window `[startMs, endMs)`. `title` is a short heading (null → fall back to
 * the first line of `contentMd`); `contentMd` is markdown (rendered as plain
 * pre-wrap text in the current prototype). No `frameKey` / `model` /
 * `confidence` — those are internal-only and never served here.
 */
export interface VisualSegmentView {
  /** Editor-visible row id used by the owner-scoped content PATCH. */
  id: number
  startMs: number
  endMs: number
  title: string | null
  contentMd: string
}

/**
 * One 錄音標記 (recording marker) dropped during capture (mobile "標記"
 * button) — a single timestamp, not a range. `label` is optional free text;
 * null → the client renders a default ("標記 N") — there is no rename/delete
 * surface today (the mobile design has none), so the server never fabricates
 * or edits this text itself. Ordered by `timeSec` ascending on the wire.
 */
export interface AudioBatchMarker {
  timeSec: number
  label: string | null
}

/**
 * `GET /audio-batches/:id/items/:itemId/segments` — the structured transcript
 * for the viewer/editor. `audioUrl` is a presigned playback URL when the audio
 * is still in the retention window; `audioExpired` is true once the 30-day R2
 * lifecycle rule has (or soon will have) purged it, so the viewer degrades to
 * a no-player read/edit surface.
 */
export interface AudioBatchSegmentsResponse {
  item: AudioBatchItem
  status: AudioBatchItemStatus
  audioUrl: string | null
  audioExpired: boolean
  /**
   * Precomputed waveform envelope (peak-per-bucket, [0,1]) from the server, or
   * null when not yet computed — the dock then decodes client-side or shows a
   * synthetic waveform.
   */
  waveformPeaks: number[] | null
  /** Current audio edit version — echoed back on the next edit for optimistic
   *  concurrency (a stale value gets a 409 stale_audio). 0 = never edited. */
  audioEditSeq?: number
  /** Per-org gate for AI voice generation (補句/重配音). Default off → the web
   *  hides the insert points + re-voice tool. Delete/export stay available. */
  ttsEditingEnabled?: boolean
  /**
   * Presigned playback URL for the SOURCE video when this job was transcribed
   * from video (grounded video transcription) — drives the two-column editor
   * with the video stage. Absent / null → the item is audio-only and the editor
   * keeps its unchanged audio layout (no field ⇒ audio, so old responses are
   * safe). Independent of `audioUrl`, which always points at the extracted audio.
   */
  videoUrl?: string | null
  /**
   * Current editor media mode. `video` requires an explicit irreversible
   * transition before duration-changing audio edits; `audio` means that gate is
   * already cleared. The visual layout still keys off `videoUrl`, so an expired
   * video safely renders the audio-style surface. Absent → treat as `'audio'`.
   */
  mediaKind?: MediaKind
  /**
   * AI-proposed corrections grounded against the video's on-screen text, for
   * human review in the editor. Absent / empty → no review UI.
   */
  suggestions?: TranscriptSuggestion[]
  /**
   * Explicit capability for transactionally applying a suggestion and storing
   * its disposition. Absent on older APIs, where suggestions stay reference-only
   * so a rolling deploy cannot partially accept a card without updating its cue.
   */
  suggestionReviewMode?: 'atomic'
  /**
   * On-screen (visual) transcript segments, synced to playback under the video
   * in the editor. Absent / empty → the item is audio-only or an un-grounded
   * video job, and the visual track is hidden. Ordered by `startMs`.
   */
  visualSegments?: VisualSegmentView[]
  speakers: TranscriptSpeaker[]
  segments: TranscriptSegmentView[]
  /**
   * 錄音標記 (recording markers), ordered by `timeSec` ascending. Always
   * present — `[]` when the item has none — so a client on today's API never
   * has to special-case an absent key the way `visualSegments`/`suggestions`
   * require.
   */
  markers: AudioBatchMarker[]
}

/**
 * Body of a rejected batch submission / staging call (422). The union covers
 * BOTH emitters of these shapes: the server's authoritative validator
 * (`validateAudioBatchUpload`, plus the controller's inline template /
 * operation-id checks) AND the web client's optimistic pre-upload filter
 * (`file-validation.ts`), which reuses the same kinds so one renderer serves
 * both. `empty_file` is client-optimistic only — the server folds empty files
 * into `invalid_file`.
 */
export interface AudioBatchValidationErrorBody {
  errors: AudioBatchValidationError[]
}

export type AudioBatchValidationError =
  | { kind: 'no_files' }
  | { kind: 'too_many_files'; received: number; max: number }
  | { kind: 'file_too_large'; filename: string; size: number; max: number }
  | { kind: 'total_size_too_large'; size: number; max: number }
  | { kind: 'unsupported_format'; filename: string; mimeType: string | null }
  | { kind: 'empty_file'; filename: string }
  | { kind: 'invalid_file'; filename: string; reason: string }
  | { kind: 'invalid_enhancement_hints'; reason: string; max?: number }
  | { kind: 'invalid_visual_context' }
  | { kind: 'visual_context_requires_video' }
  | { kind: 'invalid_notes_template' }
  | { kind: 'notes_template_not_found' }
  /** `shareToTeam` was present but not a recognized boolean-ish value (the field is string-encoded — see `AudioBatchStagedSubmitRequest`). */
  | { kind: 'invalid_share_to_team' }
  /** `shareToTeam` was true but the caller's active workspace is not a team workspace. */
  | { kind: 'team_context_required' }
  /** Emitted by `POST /audio-batches/uploads/initiate` for a malformed operation id. */
  | { kind: 'invalid_operation_id' }
  /** Link mode: the link at `index` of `sourceUrls` is not a downloadable http(s) URL. */
  | { kind: 'invalid_source_url'; index: number }
  /** Link mode: more links than one submission may carry files. */
  | { kind: 'too_many_source_urls'; received: number; max: number }
  /** One submission carried both files and links. */
  | { kind: 'mixed_sources' }
  /**
   * 錄音標記 (`markers`) failed structural validation — malformed JSON, wrong
   * shape, an out-of-range time, or an oversized array/label. `reason` is a
   * stable machine code (not localized copy); `max` is present for the
   * count/length variants.
   */
  | { kind: 'invalid_markers'; reason: string; max?: number }
  /** `markers` were supplied on a batch resolving to more than one item. */
  | { kind: 'markers_require_single_file' }

/**
 * JSON body of `POST /audio-batches` in link mode: media links instead of
 * files. Encoded like {@link AudioBatchStagedSubmitRequest} —
 * `enhancementHints` is a JSON-encoded string array and `shareToTeam` the
 * string `'true'`, because the server reads both through the same parsers as
 * the multipart path. The on-screen option is not accepted with links.
 */
export interface AudioBatchSourceUrlSubmitRequest {
  sourceUrls: string[]
  language?: string
  enhancementHints?: string
  notesTemplateId?: number
  shareToTeam?: 'true'
}

/**
 * JSON body of `POST /audio-batches` in staged-upload mode: finalizes a batch
 * whose bytes were already streamed to R2 via the chunked staging calls
 * (`.../uploads/initiate` → `.../uploads/part` → `.../uploads/complete`).
 * Every large recording (mobile's usual case — a meeting easily exceeds
 * 20MB) goes through this path rather than a single multipart POST.
 *
 * `enhancementHints` is a JSON-encoded string array and `shareToTeam` the
 * string `'true'` — the server reads both through the same parsers as the
 * multipart path, so this body kind keeps their multipart encoding even
 * though it is otherwise real JSON. `markers` is the one field that differs
 * from multipart: this body kind sends the real array, not a JSON-encoded
 * string (see `validateAudioBatchMarkers`'s doc comment for why the server
 * parser accepts both shapes). A marker is a timestamp into ONE recording's
 * timeline, so the server rejects `markers` on a submission that resolves to
 * more than one file.
 */
export interface AudioBatchStagedSubmitRequest {
  /** Identifies the chunked upload operation `stagedFiles[].key` must belong to. */
  operationId: string
  /** Ticket data from the initiate/complete legs, one per uploaded file. */
  stagedFiles: Array<{
    key: string
    filename: string
    size: number
    mimeType: string | null
  }>
  /** Upload language. `taigi` routes to Breeze ASR-26 (server coerces back
   *  to `auto` unless the operator flag is on); omit/`auto` uses default STT. */
  language?: string
  enhancementHints?: string
  visualContext?: boolean
  /** 整理範本 to apply once the transcript completes. Omitted = transcript only. */
  notesTemplateId?: number
  /** Share the uploaded item with the currently active team workspace. */
  shareToTeam?: 'true'
  markers?: AudioBatchMarker[]
}

// ---------------------------------------------------------------------------
// Upload staging (`/audio-batches/uploads/*`) — measured against
// `audio_batches_controller.initiateUpload` / `uploadPart` / `completeUpload`
// / `abortUpload` and `AudioBatchUploadTicketRecord` (the tracked-operation
// model's persisted ticket shape).
// ---------------------------------------------------------------------------

/** One staged file inside a multipart upload operation. */
export interface AudioBatchUploadTicket {
  /** Owned staged-object key (`operations/<opId>/<file>`); proves ownership. */
  key: string
  uploadId: string
  filename: string
  size: number
  mimeType: string
  /**
   * Presigned per-part PUT URLs — present only while direct-to-R2 upload is
   * enabled; absent means the browser falls back to the proxy PUT endpoint.
   */
  partUrls?: string[]
}

/** `POST /audio-batches/uploads/initiate` → 201. */
export interface AudioBatchMultipartInitiateResponse {
  operationId: string
  chunkSizeBytes: number
  uploads: AudioBatchUploadTicket[]
}

/** `PUT /audio-batches/uploads/part` → 200 (proxy fallback per-part upload). */
export interface AudioBatchPartUploadResponse {
  etag: string
}

/** `POST /audio-batches/uploads/complete` → 200. */
export interface AudioBatchCompleteUploadResponse {
  ok: true
  key: string
}

/**
 * `POST /audio-batches/uploads/abort` → 200. The abort outcome mirrors the
 * tracked operation state: `not_found` (unknown/expired operation), `active`
 * (lease still live and abort was not forced), `aborted`, or `submitted` with
 * the batch the operation already produced.
 */
export type AudioBatchAbortUploadResponse = { ok: true } & (
  | { status: 'not_found' }
  | { status: 'active' }
  | { status: 'aborted' }
  | { status: 'submitted'; batchId: number }
)

// ---------------------------------------------------------------------------
// Item-level mutations on an existing batch. Measured against
// `audio_batches_controller` (share/unshare/delete/purge/abandon/retry/
// review/split/merge).
// ---------------------------------------------------------------------------

/** `POST|DELETE /audio-batches/:id/items/:itemId/share` → 200. */
export interface AudioBatchItemShareResponse {
  item: AudioBatchItem
}

/**
 * `DELETE /audio-batches/:id` and `DELETE .../items/:itemId`. A batch/item
 * whose rows are still processing cannot hard-delete — the endpoint CANCELS
 * the in-flight work instead and says so via `cancelled`.
 */
export type AudioBatchDeleteResult = { deleted: true } | { deleted: false; cancelled: true }

/** `DELETE /audio-batches/:id/items/:itemId/audio` → 200 (keep transcript). */
export interface AudioBatchAudioPurgeResponse {
  audioPurged: true
}

/** `DELETE /audio-batches/:id/items/:itemId/video` → 200 (detach source video). */
export interface AudioBatchVideoAbandonResponse {
  videoAbandoned: true
  mediaKind: 'audio'
  videoUrl: null
  visualSegments: VisualSegmentView[]
}

/** `POST /audio-batches/:id/items/:itemId/retry` → 200 (re-enqueued). */
export interface AudioBatchRetryResponse {
  status: AudioBatchItemStatus
}

/** Disposition stored for a reviewed {@link TranscriptSuggestion}. */
export type TranscriptSuggestionDisposition = 'accepted' | 'rejected'

/** `PATCH .../suggestions/:suggestionId` → 200. */
export interface TranscriptSuggestionReviewResponse {
  id: number
  status: TranscriptSuggestionDisposition
  /**
   * Returned for accepted decisions: the server-authoritative cue text after
   * the exact-substring replace, so the editor adopts it instead of guessing.
   */
  segment?: TranscriptSegmentView
}

/**
 * Result of a structural segment edit (`.../segments/:segmentId/split|merge`):
 * the item's FULL updated segment list (re-indexed) + `focusId`, the segment
 * the editor should select after (the new right half on split, the kept
 * segment on merge).
 */
export interface AudioBatchSegmentStructureResponse {
  segments: TranscriptSegmentView[]
  focusId: number
}

/**
 * The upload contract's limits and format allowlist. These are RUNTIME
 * RE-EXPORTS from `@kiao-voice/core` — the single source of truth, chosen
 * because apps/api may not import runtime values from this package
 * (machine-enforced by `apps/api/eslint.config.js`) and the authoritative
 * validator is one of the callers. They live in this module's surface so web
 * import paths stay stable; do NOT re-declare any of them here.
 *
 * The hard boundary is still the server: the browser-side copy only powers the
 * optimistic "this won't fly" filter so the user doesn't pay round-trip latency
 * to be told no.
 */
export {
  AUDIO_BATCH_ALLOWED_EXTENSIONS,
  AUDIO_BATCH_ENHANCEMENT_HINT_SAFE_REGEX,
  AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
  AUDIO_BATCH_MAX_ENHANCEMENT_HINT_CHARS,
  AUDIO_BATCH_MAX_FILES,
  AUDIO_BATCH_MAX_FILE_BYTES,
  AUDIO_BATCH_MAX_TOTAL_FILE_BYTES,
  AUDIO_BATCH_VIDEO_EXTENSIONS,
  MIME_TO_AUDIO_BATCH_EXTENSION,
  audioExtensionFromMimeAndName,
  type AudioBatchExtension,
} from '@kiao-voice/core'
